import Modal from "react-bootstrap/Modal";
import { useForm } from "react-hook-form";
import { useShoppingCart } from "../context/ShoppingCartContext";
import RenderChildren from "../RenderChildren";
import { useAppSelector } from "../../../store/hooks";
import usePageLanguage from "../hooks/usePageLanguage";
import React from "react";
import {
  getTemplateLinkedComponent,
  getTemplateSourceComponent,
} from "../utilities/templateHelpers";
import { defaultComponents } from "../interfaces/DefaultComponents";
import { InlineComponentsMapProvider } from "../context/InlineComponentsMapContext";
import {
  getCheckoutStepNumbers,
  getConfirmOrderButtonText,
  useCheckoutConfig,
} from "./checkoutConfig";
import CheckoutLegalConsentLinks from "./CheckoutLegalConsentLinks";

interface Props {
  show: boolean;
  setShow: (val: boolean) => void;
  onCancel?: () => void;
}

const languageTranslations = {
  et: {
    agree_terms: "TINGIMUSTEGA TUTVUMINE",
    loading: "LAADIMINE...",
  },
  en: {
    agree_terms: "AGREEING TO TERMS",
    loading: "LOADING...",
  },
};

export default function CheckoutConfirmModal({
  show,
  setShow,
  onCancel,
}: Props) {
  const locale = usePageLanguage();
  const checkoutConfig = useCheckoutConfig();
  const { confirm: confirmStep } = getCheckoutStepNumbers(checkoutConfig);
  const { orderPay } = useShoppingCart();

  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const templates = useAppSelector((state) => state.builder.templates);
  const components = useAppSelector(
    (state) => state.builder.structure.components
  );
  const template = templates.find((x: any) => x.use === "checkoutTerms");
  const termsTemplateComponent =
    getTemplateLinkedComponent(components, template) ??
    getTemplateSourceComponent(template as any);
  const TermsTemplateComponent = termsTemplateComponent
    ? defaultComponents[termsTemplateComponent.type]
    : null;
  const inlineComponentsMap =
    template?.components && typeof template.components === "object"
      ? template.components
      : null;
  let termschildren: any = [];

  if (template) {
    termschildren = [
      {
        id: "terms",
        child_uuid: template.rootComponentUuid,
      },
    ];
  }

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm({
    mode: "all",
  });

  const onSubmit = () => {
    setIsSubmitting(true);
    orderPay(() => {
      setIsSubmitting(false);
      setShow(false);
    });
  };

  const onError = (data: any) => {
    console.log(data);
  };

  const handleHide = () => {
    if (onCancel) {
      onCancel();
    } else {
      setShow(false);
    }
  };

  return (
    <Modal
      show={show}
      onHide={handleHide}
      contentClassName="modal-600 checkoutConfirmModal shop-controlled-border-radius"
    >
      <Modal.Header
        closeButton
        style={{
          borderTopLeftRadius: "var(--shop-border-radius)",
          borderTopRightRadius: "var(--shop-border-radius)",
        }}
      >
        <Modal.Title>
          <span className="badge badge-square badge-primary me-3">{confirmStep}</span>
          <span className="fw-bold">
            {" "}
            {languageTranslations[locale]?.agree_terms}
          </span>
        </Modal.Title>
      </Modal.Header>
      <form onSubmit={handleSubmit(onSubmit, onError)}>
        <Modal.Body>
          <div className="terms-section">
            {/* <TermsContent /> */}
            {TermsTemplateComponent && termsTemplateComponent ? (
              <InlineComponentsMapProvider map={inlineComponentsMap}>
                <TermsTemplateComponent
                  key={
                    termsTemplateComponent?.uuid?.trim()
                      ? termsTemplateComponent.uuid
                      : termsTemplateComponent?.id
                  }
                  {...termsTemplateComponent}
                />
              </InlineComponentsMapProvider>
            ) : (
              <RenderChildren
                key={"termschildren"}
                childWebpageComponentRelations={termschildren}
              />
            )}
          </div>
          <CheckoutLegalConsentLinks
            legalLinks={checkoutConfig.legalLinks}
            locale={locale}
          />
        </Modal.Body>
        <Modal.Footer className="">
          <button
            type="submit"
            disabled={isSubmitting}
            style={{
              color: "#fff",
              fontWeight: "600",
            }}
            className="btn  w-100 shop-controlled-color shop-controlled-border-radius"
          >
            {isSubmitting ? (
              <div className="d-flex align-items-center justify-content-center">
                <div
                  className="spinner-border spinner-border-sm me-2"
                  role="status"
                >
                  <span className="visually-hidden">
                    {languageTranslations[locale]?.loading}
                  </span>
                </div>
                {languageTranslations[locale]?.loading}
              </div>
            ) : (
              getConfirmOrderButtonText(locale, checkoutConfig)
            )}
          </button>
        </Modal.Footer>
      </form>
    </Modal>
  );
}
